/*eslint no-undef: "off"*/
import MojitoCore from 'mojito/core';
import {settingsStorage} from 'mojito/core/services/system-settings/slice';

import {bindAll, fetchInternalResource, isJsonContentType} from '#core/utils/utils.js';
import {assignRecursively} from '#core/utils/config-utils.js';

import {DebugFlags} from '#core/application/debug/debugFlags.js';

const StringUtils = MojitoCore.Base.StringUtils;

//Set en-GB locale for 'us' language as a workaround to prevent an issue with time like 24:30.
//This workaround could be removed after this bug is fixed in chrome:
//https://support.google.com/chrome/thread/29828561/chrome-80-shows-timestamp-24-xx-instead-of-00-00?hl=en
//https://github.com/moment/luxon/issues/726
const bcp47LanguageTagsMap = {
    en: 'en-GB',
    us: 'en-GB',
    'en-GB': 'en-GB',
    'en-US': 'en-GB',
};

// one and only translation key that has to be presented for all available languages in sportsbook
const staticTranslations = {
    LANGUAGE_NATIVE_NAME: {
        // Language native name, i.e. English, Svenska, etc.
        'az-AZ': 'Azərbaycan',
        'en-GB': 'English',
        'en-US': 'English(US)',
        'es-ES': 'Español',
        'es-CO': 'Español(CO)',
        'es-MX': 'Español(MX)',
        'es-PA': 'Español(PA)',
        'fr-FR': 'Français',
        'ga-IE': 'Irish',
        'nl-NL': 'Nederlands',
        'pt-PT': 'Portuguese',
        'ru-RU': 'Русский',
        'sv-SE': 'Engelska',
    },
};

function recursivelyMapPhrases(obj, fn) {
    for (const key in obj) {
        if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
        if (typeof obj[key] === 'string') {
            obj[key] = fn(key, obj[key]);
        } else if (typeof obj[key] === 'object') {
            recursivelyMapPhrases(obj[key], fn);
        }
    }
}

const LANGUAGES = OPERATIONAL_LANGUAGES;

class LocalizationClass {
    #requestedLanguage;

    constructor() {
        bindAll(this, 'getTranslation', 'getCurrentTranslations');
    }

    init(forcedLanguage) {
        this.fallbackDictionaries = FALLBACK_DICTIONARIES;
        this.availableDictionaries = AVAILABLE_DICTIONARIES;
        this.availableLanguages = LANGUAGES;

        /*
        Hacking Mojito.

        We need `enableUrlLanguage` to be `true` to let `settingsStorage.getAppLanguage` to properly work.
        In particular, to parse the language flag from URL in `SystemSettingsStorage.getLanguageFromUrl()`.

        Mojito set `enableUrlLanguage` themselves, but they do it too late.
        We run `Localization.init()` before Mojito run `SystemSettingsStorage.configure`.
         */
        settingsStorage.enableUrlLanguage = true;

        this.#requestedLanguage = forcedLanguage || settingsStorage.getAppLanguage() || this.availableDictionaries[0];
        // Current language is not obligated to be the same as requestedLanguage.
        // It can be different if we don't have translations for the requestedLanguage,
        // or backend format is different from specified in the appLanguage.

        // If host application wants language that we don't have translations for
        // it will look for the best candidate
        const lang = findMatchingLang(this.#requestedLanguage);

        this.currentLanguage = lang.contentLocale;
        this.currentDictionaryLang = lang.dictionary;

        this.translations = LANGUAGES.reduce((res, lang) => {
            res[lang.contentLocale] = Object.entries(staticTranslations).reduce((reducer, [trKey, trValue]) => {
                reducer[trKey] =
                    trValue[lang.labelDictionary] ||
                    trValue[this.fallbackDictionaries[lang.labelDictionary]] ||
                    trValue[lang.dictionary] ||
                    trValue[this.fallbackDictionaries[lang.dictionary]];

                return reducer;
            }, {});

            return res;
        }, {});

        const fileName = this.currentDictionaryLang; // it is always 4-letter for now

        return fetchTranslationsFile(fileName).then(translations => {
            assignRecursively(this.translations[this.currentLanguage], translations || {});

            if (DebugFlags.wrapPhrases || DebugFlags.keysAsPhrases) {
                const modifyPhrase = DebugFlags.wrapPhrases ? (key, phrase) => `*${phrase}*` : key => key;

                for (const langKey in this.translations) {
                    if (Object.prototype.hasOwnProperty.call(this.translations, langKey)) {
                        const langDict = this.translations[langKey];
                        recursivelyMapPhrases(langDict, modifyPhrase);
                    }
                }
            }
        });
    }

    configure(translations) {
        if (!translations) return;
        LANGUAGES.forEach(lang => {
            const dict = translations[lang.dictionary];

            assignRecursively(this.translations[lang.contentLocale], dict || {});
        });
        // assignRecursively(this.translations[this.currentLanguage], translations[this.currentDictionaryLang]);
    }

    getMojitoConfig() {
        return {
            languages: this.translations,
        };
    }

    getAvailableLanguages() {
        return this.availableLanguages;
    }

    getCurrentLanguage() {
        return this.currentLanguage;
    }

    getCurrentDictionaryLang() {
        return this.currentDictionaryLang;
    }

    getCurrentLanguageCode() {
        return this.currentLanguage.split('-')[0];
    }

    getCurrentLocale() {
        return bcp47LanguageTagsMap[this.currentLanguage];
    }

    getCurrentTranslations() {
        return this.translations[this.currentLanguage];
    }

    getTranslation(key, ...args) {
        if (args.length > 0) {
            return StringUtils.resolveAndFormatString(key, this.getCurrentTranslations(), ...args);
        }

        return StringUtils.resolveString(key, this.getCurrentTranslations());
    }
}

function findMatchingLang(langToFind) {
    for (const lang of LANGUAGES) {
        if (lang.aliases && lang.aliases.includes(langToFind)) {
            return lang;
        }
    }

    langToFind = langToFind.replace('_', '-').replace(' ', '').toLowerCase();

    for (const lang of LANGUAGES) {
        if (lang.dictionary.toLowerCase() === langToFind) {
            return lang;
        }
        if (lang.contentLocale.toLowerCase() === langToFind) {
            return lang;
        }
        if (lang.aliases && lang.aliases.includes(langToFind)) {
            return lang;
        }
    }
    // If we don't have the requested language, try to guess the best candidate.

    // If the requested language is 2-letter, we will try to find a dictionary with the same prefix.
    if (langToFind.length === 2) {
        for (const lang of LANGUAGES) {
            if (lang.dictionary.split('-')[0].toLowerCase() === langToFind) {
                return lang;
            }
            if (lang.contentLocale.split('-')[0].toLowerCase() === langToFind) {
                return lang;
            }
        }
    }

    // Didn't find a match, return the first available language
    return LANGUAGES[0];
}

export async function fetchTranslationsFile(lang) {
    const response = await fetchInternalResource(`translations/${lang}`, {credentials: 'include'});
    const isJson = isJsonContentType(response);

    if (!response.ok || !isJson) {
        const message = `Translations file loading failed (${lang}).`;

        throw new Error(
            isJson
                ? `${message} Response status: ${response.status} ${response.statusText}`
                : `${message} Content-type is not 'application/json'`
        );
    }

    return await response.json();
}

export const Localization = new LocalizationClass();
